"use client";

import { useEffect } from "react";

/**
 * Globalna granica błędu — przejmuje sterowanie, gdy błąd wystąpi w samym
 * głównym layoucie (root layout). Zastępuje `<html>`/`<body>`, więc globalny
 * CSS (Tailwind) NIE jest tu dostępny — używamy stylów inline, aby strona
 * wyglądała poprawnie niezależnie od stanu aplikacji.
 */

const STATUS_PAGE_URL =
  process.env.NEXT_PUBLIC_STATUS_URL || "https://status.nextschool.pl";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    console.error(error);
  }, [error]);

  return (
    <html lang="pl" translate="no">
      <head>
        <meta name="google" content="notranslate" />
        <title>Wystąpił błąd · NextSchool</title>
      </head>
      <body
        style={{
          margin: 0,
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "48px 24px",
          background:
            "linear-gradient(135deg, #F8FAFC 0%, #ffffff 45%, #EEF2FF 100%)",
          fontFamily:
            "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
          color: "#0F172A",
        }}
      >
        <div style={{ maxWidth: 520, width: "100%", textAlign: "center" }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 16px",
              borderRadius: 9999,
              background: "rgba(255,255,255,0.7)",
              border: "1px solid rgba(244,63,94,0.35)",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
              fontSize: 12,
              fontWeight: 600,
              color: "#e11d48",
              marginBottom: 28,
            }}
          >
            Wystąpił nieoczekiwany błąd
          </div>

          <div
            style={{
              width: 88,
              height: 88,
              margin: "0 auto 24px",
              borderRadius: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg, #3B5BDB, #5b6ee8 55%, #8b5cf6)",
              boxShadow: "0 10px 30px rgba(59,91,219,0.25)",
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="44"
              height="44"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ffffff"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" />
              <path d="M12 9v4" />
              <path d="M12 17h.01" />
            </svg>
          </div>

          <h1 style={{ margin: "0 0 12px", fontSize: 28, fontWeight: 700 }}>
            Coś poszło nie tak
          </h1>
          <p
            style={{
              margin: "0 auto",
              maxWidth: 400,
              fontSize: 16,
              lineHeight: 1.6,
              color: "#475569",
            }}
          >
            Napotkaliśmy nieoczekiwany problem. Spróbuj odświeżyć stronę — jeśli
            błąd się powtarza, sprawdź stan naszych usług.
          </p>

          {error?.digest && (
            <p
              style={{
                margin: "12px 0 0",
                fontSize: 12,
                color: "#94a3b8",
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              }}
            >
              Kod błędu: {error.digest}
            </p>
          )}

          <div
            style={{
              marginTop: 36,
              display: "flex",
              flexWrap: "wrap",
              gap: 12,
              justifyContent: "center",
            }}
          >
            <button
              onClick={() => reset()}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 24px",
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                background: "linear-gradient(90deg, #3B5BDB, #5b6ee8)",
                color: "#ffffff",
                fontSize: 14,
                fontWeight: 600,
                boxShadow: "0 10px 20px rgba(59,91,219,0.25)",
              }}
            >
              Spróbuj ponownie
            </button>
            <a
              href="/"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "12px 24px",
                borderRadius: 12,
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                color: "#334155",
                fontSize: 14,
                fontWeight: 600,
                textDecoration: "none",
              }}
            >
              Strona główna
            </a>
          </div>

          <div style={{ marginTop: 32, display: "flex", justifyContent: "center" }}>
            <a
              href={STATUS_PAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 12,
                padding: "14px 16px",
                borderRadius: 16,
                background: "rgba(255,255,255,0.7)",
                border: "1px solid rgba(226,232,240,0.7)",
                boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                textDecoration: "none",
                textAlign: "left",
              }}
            >
              <span
                style={{
                  display: "flex",
                  width: 36,
                  height: 36,
                  flexShrink: 0,
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 12,
                  background: "rgba(16,185,129,0.1)",
                  color: "#059669",
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <path d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2" />
                </svg>
              </span>
              <span style={{ minWidth: 0 }}>
                <span
                  style={{
                    display: "block",
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#1e293b",
                  }}
                >
                  Sprawdź dostępność naszych usług
                </span>
                <span style={{ display: "block", fontSize: 12, color: "#64748b" }}>
                  Zobacz, czy to chwilowa awaria po naszej stronie.
                </span>
              </span>
            </a>
          </div>

          <p style={{ marginTop: 40, fontSize: 12, color: "#94a3b8" }}>
            NextSchool · Platforma edukacyjna
          </p>
        </div>
      </body>
    </html>
  );
}
